import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { HoverInsight } from '../types';
import { Tag, Users, Search, Sparkles, Wallet, Smartphone, Undo2, HandCoins, Flame } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip as ChartTooltip, ResponsiveContainer, CartesianGrid } from 'recharts';
import api from '../lib/api';
import { formatIST, formatChartDate } from '../lib/formatDate';
import { DateRangePicker } from '../components/DateRangePicker';
import { useGlobalDateRange, clampRangeToBounds } from '../context/GlobalDateRangeContext';
import { Progress as ProgressBase } from '../components/ui/progress';

const Progress: any = ProgressBase;

interface ShopifyTagsProps {
  onHover: (insight: HoverInsight | null) => void;
}

interface OrderTagHighlight {
  tag: string;
  label: string;
  count: number;
  revenue: number;
  net_sales: number;
  revenue_pct: number;
  order_pct: number;
  net_sales_pct: number;
}

interface OrderTagRow {
  tag: string;
  count: number;
  revenue: number;
  net_sales: number;
  revenue_pct: number;
}

interface NitroHiIntendOrder {
  order_name: string;
  date: string;
  revenue: number;
}

interface NitroHiIntendSummary {
  count: number;
  revenue: number;
  orders: NitroHiIntendOrder[];
}

interface TagsData {
  connected: boolean;
  period_label: string;
  products_sold_in_range: number;
  customers_active_in_range: number;
  total_products: number;
  total_customers: number;
  product_tag_counts: Record<string, number>;
  customer_tag_counts: Record<string, number>;
  received_at: string | null;
  total_order_revenue: number;
  total_net_sales: number;
  total_orders_in_range: number;
  order_tag_highlights: OrderTagHighlight[];
  order_tags_all: OrderTagRow[];
  nitro_hi_intend: NitroHiIntendSummary;
}

const inr = (n: number) => `₹${(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const toISO = (d: Date) => format(d, 'yyyy-MM-dd');

const HIGHLIGHT_ICONS: Record<string, any> = {
  'assisted revenue': HandCoins,
  'abandoned recovered': Undo2,
  magic: Sparkles,
  upi: Smartphone,
  wallet: Wallet,
  'abandoned cart- t': Undo2,
};

const toChartData = (counts: Record<string, number>, search: string) =>
  Object.entries(counts || {})
    .filter(([name]) => name.toLowerCase().includes(search.toLowerCase()))
    .sort((a, b) => b[1] - a[1])
    .slice(0, 12)
    .map(([name, count]) => ({ name, count }));

export default function ShopifyTags({ onHover }: ShopifyTagsProps) {
  const [data, setData] = useState<TagsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const today = useMemo(() => new Date(), []);
  const ninetyDaysAgo = useMemo(() => { const d = new Date(); d.setDate(d.getDate() - 90); return d; }, []);
  const { globalRange } = useGlobalDateRange();
  const [range, setRange] = useState<{ from: Date; to: Date }>(() => clampRangeToBounds(globalRange, ninetyDaysAgo, today));

  useEffect(() => {
    setLoading(true);
    api.get('/cx/shopify-tags', { params: { start_date: toISO(range.from), end_date: toISO(range.to) } })
      .then((res) => setData(res.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [range]);

  const productChart = useMemo(() => toChartData(data?.product_tag_counts || {}, search), [data, search]);
  const customerChart = useMemo(() => toChartData(data?.customer_tag_counts || {}, search), [data, search]);
  const orderTagsFiltered = useMemo(
    () => (data?.order_tags_all || []).filter((r) => r.tag.toLowerCase().includes(search.toLowerCase())),
    [data, search]
  );

  return (
    <div className="space-y-8 animate-fade-in font-sans -m-6 md:-m-8 p-6 md:p-8 min-h-full" style={{ background: 'linear-gradient(180deg, #F7EFE3 0%, #F3E8D8 100%)' }} data-testid="shopify-tags-module-root">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-[#e2d3ba] pb-6">
        <div>
          <span className="text-[10px] font-mono uppercase tracking-widest text-[#9b7d54] block mb-1">Live via Shopify Admin API</span>
          <h1 className="text-3xl font-semibold font-arial text-slate-800 tracking-tight">Shopify Tags — Products & Customers</h1>
          <p className="text-xs text-slate-500 mt-1 font-sans">Tag frequency for products sold and customers active in the selected date range.</p>
        </div>
        <div className="flex items-center gap-3">
          <DateRangePicker value={range} onChange={setRange} minDate={ninetyDaysAgo} maxDate={today} testIdPrefix="shopify-tags" />
          <div
            data-testid="shopify-tags-connection-badge"
            className={`px-4 py-2.5 rounded-xl flex items-center gap-2.5 text-xs font-semibold border ${
              data?.connected ? 'bg-blue-50 text-blue-700 border-blue-100' : 'bg-slate-100 text-slate-400 border-slate-200'
            }`}
          >
            <Tag className="h-4.5 w-4.5" />
            <span>{data?.connected ? `Synced ${data.received_at ? formatIST(data.received_at) : ''}` : 'Not Connected'}</span>
          </div>
        </div>
      </div>

      {loading ? (
        <div data-testid="shopify-tags-loading" className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm text-xs text-slate-400 font-mono">Loading…</div>
      ) : !data?.connected ? (
        <div data-testid="shopify-tags-disconnected" className="bg-white border border-[#e8dcc9] rounded-3xl p-6 shadow-sm text-xs text-slate-500">
          No tag data yet — connect Shopify in Admin Console → Integrations to sync the product and customer catalog.
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white border border-[#e8dcc9] rounded-3xl p-5 shadow-sm">
              <p className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest mb-2">Products Sold in Range</p>
              <p className="text-2xl font-bold text-slate-800">{data.products_sold_in_range}</p>
              <p className="text-[10px] text-slate-400 mt-1">of {data.total_products} total</p>
            </div>
            <div className="bg-white border border-[#e8dcc9] rounded-3xl p-5 shadow-sm">
              <p className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest mb-2">Customers Active in Range</p>
              <p className="text-2xl font-bold text-slate-800">{data.customers_active_in_range}</p>
              <p className="text-[10px] text-slate-400 mt-1">of {data.total_customers} total</p>
            </div>
            <div className="bg-white border border-[#e8dcc9] rounded-3xl p-5 shadow-sm">
              <p className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest mb-2">Unique Product Tags</p>
              <p className="text-2xl font-bold text-slate-800">{Object.keys(data.product_tag_counts).length}</p>
            </div>
            <div className="bg-white border border-[#e8dcc9] rounded-3xl p-5 shadow-sm">
              <p className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest mb-2">Unique Customer Tags</p>
              <p className="text-2xl font-bold text-slate-800">{Object.keys(data.customer_tag_counts).length}</p>
            </div>
          </div>

          <div className="relative max-w-sm">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <input
              data-testid="shopify-tags-search-input"
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search product, customer & order tags…"
              className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-slate-200 bg-white text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-300"
            />
          </div>

          <div className="space-y-5" data-testid="shopify-order-tags-section">
            <div>
              <h2 className="font-display font-semibold text-slate-700 text-sm tracking-wide uppercase">Order Tags — Business Impact</h2>
              <p className="text-[11px] text-slate-400 mt-0.5">Pulled directly from the Orders API's own 'tags' field, across every order status (paid, pending, cod, etc.). Total Revenue is Shopify's raw order total; Net Sales subtracts discounts and refunds.</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4" data-testid="shopify-order-tags-highlights">
              {(data.order_tag_highlights || []).map((h) => {
                const Icon = HIGHLIGHT_ICONS[h.tag] || Tag;
                return (
                  <div key={h.tag} className="bg-white border border-[#e8dcc9] rounded-3xl p-5 shadow-sm space-y-3" data-testid={`shopify-order-tag-${h.tag.replace(/\s+/g, '-')}`}>
                    <div className="flex items-center gap-2">
                      <span className="h-8 w-8 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center">
                        <Icon className="h-4 w-4" />
                      </span>
                      <span className="text-xs font-semibold text-slate-700">{h.label}</span>
                    </div>
                    <div>
                      <p className="text-[9px] font-mono uppercase text-slate-400">Total Revenue</p>
                      <p className="text-xl font-bold text-slate-800">{inr(h.revenue)}</p>
                      <p className="text-[10px] text-emerald-600 font-semibold mt-0.5">Net Sales: {inr(h.net_sales)}</p>
                      <p className="text-[10px] text-slate-400 mt-0.5">{h.count.toLocaleString('en-IN')} orders · {h.order_pct.toFixed(1)}% of orders</p>
                    </div>
                    <div className="space-y-1">
                      <Progress value={h.revenue_pct} className="h-1.5" data-testid={`shopify-order-tag-${h.tag.replace(/\s+/g, '-')}-progress`} />
                      <p className="text-[10px] font-mono text-slate-400">{h.revenue_pct.toFixed(1)}% of total revenue</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="bg-white border border-[#e8dcc9] rounded-3xl p-5 shadow-sm" data-testid="shopify-nitro-hi-intend-tile">
              <div className="flex items-center gap-2 mb-3">
                <span className="h-8 w-8 rounded-full bg-orange-50 text-orange-600 flex items-center justify-center">
                  <Flame className="h-4 w-4" />
                </span>
                <div>
                  <span className="text-xs font-semibold text-slate-700">Nitro Hi-Intend</span>
                  <p className="text-[10px] text-slate-400">One-time historical fetch — full order history, not limited to the 90-day window</p>
                </div>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
                <div className="flex gap-8">
                  <div>
                    <p className="text-[9px] font-mono uppercase text-slate-400">Orders</p>
                    <p className="text-xl font-bold text-slate-800" data-testid="nitro-hi-intend-count">{data.nitro_hi_intend?.count ?? 0}</p>
                  </div>
                  <div>
                    <p className="text-[9px] font-mono uppercase text-slate-400">Revenue Impact</p>
                    <p className="text-xl font-bold text-slate-800" data-testid="nitro-hi-intend-revenue">{inr(data.nitro_hi_intend?.revenue || 0)}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(data.nitro_hi_intend?.orders || []).map((o) => (
                    <span key={o.order_name} className="text-[10px] font-mono px-2.5 py-1 rounded-full bg-orange-50 text-orange-700 border border-orange-100">
                      {o.order_name} · {formatChartDate(o.date, 'dd MMM yyyy')} · {inr(o.revenue)}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-white border border-[#e8dcc9] p-6 rounded-3xl shadow-sm space-y-4" data-testid="shopify-order-tags-all">
              <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest">All Order Tags, Ranked by Revenue</span>
              {orderTagsFiltered.length > 0 ? (
                <div className="space-y-3">
                  {orderTagsFiltered.map((r) => (
                    <div key={r.tag} className="space-y-1">
                      <div className="flex items-center justify-between text-xs">
                        <span className="font-medium text-slate-700">{r.tag}</span>
                        <span className="text-slate-500 font-mono">{inr(r.revenue)} rev · {inr(r.net_sales)} net · {r.count.toLocaleString('en-IN')} orders</span>
                      </div>
                      <Progress value={r.revenue_pct} className="h-1.5" />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-slate-400 py-6 text-center">{search ? 'No matching order tags.' : 'No order tags found in this window.'}</p>
              )}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white border border-[#e8dcc9] p-6 rounded-3xl shadow-sm space-y-4" data-testid="shopify-tags-product-chart">
              <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest">Top Product Tags</span>
              <div className="h-80 w-full">
                {productChart.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={productChart} layout="vertical" margin={{ top: 10, right: 20, left: 20, bottom: 0 }}>
                      <CartesianGrid stroke="rgba(0,0,0,0.02)" strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" stroke="#64748b" fontSize={10} tickLine={false} />
                      <YAxis type="category" dataKey="name" stroke="#64748b" fontSize={11} tickLine={false} width={100} />
                      <ChartTooltip contentStyle={{ background: '#ffffff', borderColor: '#e2e8f0', borderRadius: '12px' }} />
                      <Bar dataKey="count" fill="#1d4ed8" radius={[0, 4, 4, 0]} name="Products" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-xs text-slate-400 py-10 text-center">{search ? 'No matching product tags.' : 'No product tags found in this window.'}</p>
                )}
              </div>
            </div>
            <div className="bg-white border border-[#e8dcc9] p-6 rounded-3xl shadow-sm space-y-4" data-testid="shopify-tags-customer-chart">
              <span className="text-[10px] font-mono font-bold text-slate-400 uppercase tracking-widest flex items-center gap-1.5">
                <Users className="h-3 w-3" /> Top Customer Tags
              </span>
              <div className="h-80 w-full">
                {customerChart.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={customerChart} layout="vertical" margin={{ top: 10, right: 20, left: 20, bottom: 0 }}>
                      <CartesianGrid stroke="rgba(0,0,0,0.02)" strokeDasharray="3 3" horizontal={false} />
                      <XAxis type="number" stroke="#64748b" fontSize={10} tickLine={false} />
                      <YAxis type="category" dataKey="name" stroke="#64748b" fontSize={11} tickLine={false} width={100} />
                      <ChartTooltip contentStyle={{ background: '#ffffff', borderColor: '#e2e8f0', borderRadius: '12px' }} />
                      <Bar dataKey="count" fill="#0ea5e9" radius={[0, 4, 4, 0]} name="Customers" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <p className="text-xs text-slate-400 py-10 text-center">{search ? 'No matching customer tags.' : 'No customer tags found yet.'}</p>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
